import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Trophy, Send, Users, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Coach-facing full weekly challenge view.
 * Mirrors the dashboard widget data but renders the COMPLETE list of athletes
 * (top performers + missing) instead of truncating to 3/5. Reuses the same
 * backend endpoint `/challenges/coach/widget`.
 */
const CoachWeeklyChallenge = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/challenges/coach/widget`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load challenge data');
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const remind = async (athleteId) => {
    try {
      await axios.post(`${API}/challenges/coach/remind?athlete_id=${athleteId}`, {}, { headers: getAuthHeaders() });
      toast.success('Reminder sent');
    } catch (e) { toast.error('Failed to send reminder'); }
  };

  if (loading) return <div className="text-muted-foreground text-sm" data-testid="challenge-loading">Loading…</div>;
  if (!data?.challenge) {
    return (
      <div className="space-y-4" data-testid="challenge-empty">
        <Link to="/coach" className="inline-flex items-center text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground">
          <ArrowLeft size={14} className="mr-1" /> Back to Dashboard
        </Link>
        <div className="bg-card border border-border/40 rounded-sm p-8 text-center">
          <Trophy className="text-muted-foreground mx-auto mb-3" size={40} />
          <p className="text-sm">No active weekly challenge.</p>
        </div>
      </div>
    );
  }

  const { challenge, team_size, completed, completion_rate, top_performers, missing } = data;

  return (
    <div className="space-y-6" data-testid="coach-weekly-challenge">
      <Link to="/coach" className="inline-flex items-center text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground">
        <ArrowLeft size={14} className="mr-1" /> Back to Dashboard
      </Link>

      <div className="bg-gradient-to-br from-primary/10 via-card to-black border border-primary/30 rounded-sm p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-3">
            <Trophy className="text-primary" size={26} />
            <div>
              <h1 className="font-heading text-2xl uppercase tracking-tight">{challenge.title}</h1>
              <p className="text-xs text-muted-foreground mt-0.5">Weekly Challenge · Full Coach View</p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-heading text-4xl text-primary">{completed}<span className="text-muted-foreground text-base">/{team_size}</span></p>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{completion_rate}% participation</p>
          </div>
        </div>
        <div className="h-2 bg-muted/30 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-primary to-amber-300 transition-all" style={{ width: `${completion_rate}%` }} />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Top performers — full list */}
        <div className="bg-card border border-border/40 rounded-sm p-5" data-testid="top-performers">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1">
            <Users size={11} /> Participants ({top_performers.length})
          </p>
          {top_performers.length === 0 ? (
            <p className="text-sm text-muted-foreground">No participants yet.</p>
          ) : (
            <div className="space-y-2">
              {top_performers.map((p) => (
                <div key={p.athlete_id} className="flex items-center justify-between text-sm py-1.5 border-b border-border/20 last:border-0">
                  <span className="truncate">
                    {p.rank <= 3 ? ['🥇', '🥈', '🥉'][p.rank - 1] : <span className="text-muted-foreground">#{p.rank}</span>} {p.athlete_name}
                  </span>
                  <span className="font-heading text-primary">{p.best_score}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Missing — full list with Remind */}
        <div className="bg-card border border-border/40 rounded-sm p-5" data-testid="missing-list">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
            Haven't participated yet ({missing.length})
          </p>
          {missing.length === 0 ? (
            <p className="text-sm text-emerald-400">Everyone has participated. 🎉</p>
          ) : (
            <div className="space-y-1.5">
              {missing.map((m) => (
                <div key={m.id} className="flex items-center justify-between text-sm bg-muted/20 rounded-sm px-2.5 py-2">
                  <span className="truncate">{m.name}</span>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-primary hover:bg-primary/10" onClick={() => remind(m.id)} data-testid={`remind-full-${m.id}`}>
                    <Send size={11} className="mr-1" /> Remind
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoachWeeklyChallenge;
